import type { Loop, Mode } from '../types';
import type { Words } from '../copy';
import { fmtDur } from '../format';

interface Props {
  loops: Loop[];
  activeLoop: string | null;
  duration: number;
  mode: Mode;
  w: Words;
  canCreate: boolean;
  onCreate: () => void;
  onPlay: (l: Loop) => void;
  onSelect: (id: string) => void;
  onMix: (id: string, mixed: boolean) => void;
  onRemove: (id: string) => void;
  onMove: (id: string, edge: 'start' | 'end', delta: number) => void;
  onExport: (l: Loop) => void;
}

const STEP = 0.1;

export default function LoopList(p: Props) {
  const active = p.loops.find((l) => l.id === p.activeLoop) ?? null;

  return (
    <div className="loops">
      <div className="loops-head">
        <div className="label">{p.w.loops}</div>
        <button className="btn violet sm" onClick={p.onCreate} disabled={!p.canCreate}>
          ＋ créer depuis la sélection
        </button>
      </div>

      {p.loops.length === 0 && (
        <div className="note">
          Aucune {p.mode === 'troubadour' ? 'boucle' : 'region'} pour l’instant — sélectionne un
          passage sur la forme d’onde puis clique « créer ».
        </div>
      )}

      {p.loops.map((l) => {
        const on = l.id === p.activeLoop;
        const left = p.duration ? (l.start / p.duration) * 100 : 0;
        const width = p.duration ? ((l.end - l.start) / p.duration) * 100 : 0;
        return (
          <div key={l.id} className={`loop${on ? ' on' : ''}`}>
            <button
              className="btn xs"
              onClick={() => p.onPlay(l)}
              style={on ? { color: 'var(--green)', borderColor: 'var(--green)' } : undefined}
              aria-label={`Écouter ${l.name}`}
            >
              ▶
            </button>
            <button
              className="nm"
              onClick={() => p.onSelect(l.id)}
              style={{ background: 'none', border: 0, padding: 0, textAlign: 'left' }}
            >
              {l.name}
            </button>
            <div className="track" onClick={() => p.onSelect(l.id)}>
              <i style={{ left: `${left}%`, width: `${Math.max(1, width)}%` }} />
            </div>
            <span className="dur">{fmtDur(l.end - l.start)}</span>
            <label className="chk">
              <input
                type="checkbox"
                checked={l.mixed}
                onChange={(e) => p.onMix(l.id, e.target.checked)}
              />
              {p.w.mix}
            </label>
            <button className="btn xs ghost" onClick={() => p.onExport(l)} aria-label={`Exporter ${l.name}`}>
              ⬇
            </button>
            <button className="btn xs ghost" onClick={() => p.onRemove(l.id)} aria-label={`Supprimer ${l.name}`}>
              ✕
            </button>
          </div>
        );
      })}

      {p.mode === 'maestro' && active && (
        <div className="nudge">
          <span>{active.name} · start</span>
          <button className="btn xs" onClick={() => p.onMove(active.id, 'start', -STEP)}>−</button>
          <button className="btn xs" onClick={() => p.onMove(active.id, 'start', STEP)}>＋</button>
          <span>end</span>
          <button className="btn xs" onClick={() => p.onMove(active.id, 'end', -STEP)}>−</button>
          <button className="btn xs" onClick={() => p.onMove(active.id, 'end', STEP)}>＋</button>
          <div className="grow" />
          <span className="mono">
            {active.start.toFixed(2)} → {active.end.toFixed(2)} s
          </span>
        </div>
      )}
    </div>
  );
}
